/**
 * Cirugía oral e inclusiones dentales (protocolo CK-CIR-ORAL): planificación, lista de verificación de los tiempos
 * quirúrgicos, datos de la cirugía, fechas de control y nota de evolución. Lógica pura, sin base de datos.
 */
import type { PasoCheck } from "./implantes.ts";

export const PROCEDIMIENTOS_CIRUGIA = [
  "Tercer molar retenido o impactado",
  "Diente supernumerario",
  "Canino o diente incluido: exodoncia",
  "Ventana quirúrgica y botón o bracket para tracción",
  "Quistectomía o enucleación de lesión odontogénica con biopsia",
  "Apicectomía con obturación retrógrada",
  "Frenectomía o frenotomía",
  "Alveoloplastia, regularización ósea o torus",
] as const;
const [MOLAR, SUPERNUMERARIO, CANINO, TRACCION, QUISTE, APICECTOMIA, FRENECTOMIA, ALVEOLOPLASTIA] = PROCEDIMIENTOS_CIRUGIA;

export const CLASE_PELL_GREGORY = ["Clase I", "Clase II", "Clase III"] as const;
export const POSICION_PELL_GREGORY = ["A", "B", "C"] as const;
export const CLASIFICACION_WINTER = ["Mesioangular", "Distoangular", "Vertical", "Horizontal", "Linguoangular", "Vestibuloangular", "Invertido"] as const;
export const TIPOS_SUPERNUMERARIO = ["Mesiodens", "Paramolar", "Distomolar"] as const;
export const TIPOS_FRENECTOMIA = ["Labial", "Lingual"] as const;

export const BLOQUEOS_ANESTESICOS = [
  "N. alveolar inferior + bucal + lingual", "N. mentoniano o incisivo", "N. alveolar superior posterior", "N. alveolar superior medio o anterior", "N. palatino mayor", "N. nasopalatino",
] as const;
export const FARMACOS_ANESTESICOS = ["Articaína 4 % + epinefrina 1:100.000", "Lidocaína 2 % + epinefrina 1:100.000", "Mepivacaína 3 %", "Bupivacaína 0.5 %"] as const;
export const HOJAS_CIRUGIA = ["#15", "#15C (microdisección)", "#12 (retromolar o palatino)", "#11"] as const;
export const COLGAJOS_CIRUGIA = ["Envolvente o sulcular", "Triangular (con liberadora anterior)", "Palatino de espesor total", "Neumann modificado o trapezoidal", "Ventana directa (punch u ojal mucoso)"] as const;
export const FRESAS_CIRUGIA = ["Carburo de tungsteno Zekrya", "Fisura #702 o #703", "Redonda quirúrgica #6 o #8"] as const;
export const ODONTOSECCION = ["Sección coronaria (decapitación)", "Sección radicular (separación de raíces)", "Fragmentación cuspídea", "Osteotomía de despeje pericoronario"] as const;
export const ELEVADORES = ["Elevador recto fino", "Elevador recto ancho", "Botadores Seldin o apexo", "Elevador de Pott o Winter"] as const;
export const HEMOSTATICOS = ["Esponja de gelatina (Gelfoam)", "Celulosa oxidada regenerada", "Cera para hueso", "Fibrina rica en plaquetas (PRF)", "Sustituto óseo (preservación alveolar)"] as const;
export const HILOS_CIRUGIA = ["Seda trenzada", "Nylon monofilamento", "Ácido poliglicólico (PGA / Vicryl)", "PTFE"] as const;
export const CALIBRES_CIRUGIA = ["3-0", "4-0", "5-0"] as const;
export const PATRONES_SUTURA = ["Puntos simples separados", "Colchonero o en X", "Anclaje distal"] as const;
export const COMPLICACIONES_CIRUGIA = ["Hemorragia", "Parestesia", "Fractura radicular", "Fractura ósea o de tuberosidad", "Comunicación oroantral", "Dehiscencia de la herida"] as const;

export const TIPOS_CONTROL_CIRUGIA = ["Retiro de puntos", "Control postoperatorio", "Resultado de patología", "Control de tracción ortodóncica"] as const;
export const COMPLICACIONES_CONTROL = ["Alveolitis seca", "Infección", "Hemorragia", "Parestesia persistente", "Dehiscencia de la herida", "Trismo"] as const;

export interface PlanCirugia {
  piezas: number[]; zona: string; procedimientos: string[];
  claseMolar: string; posicionMolar: string; winter: string; supernumerario: string; frenectomia: string; notas: string;
}
export const planCirugiaVacio = (): PlanCirugia => ({ piezas: [], zona: "", procedimientos: [], claseMolar: "", posicionMolar: "", winter: "", supernumerario: "", frenectomia: "", notas: "" });

/** Pieza FDI válida: permanentes 11–48 (unidad 1–8) y temporales 51–85 (unidad 1–5). */
export function piezaCirugia(n: number): boolean {
  const q = Math.floor(n / 10), u = n % 10;
  return Number.isInteger(n) && ((q >= 1 && q <= 4 && u >= 1 && u <= 8) || (q >= 5 && q <= 8 && u >= 1 && u <= 5));
}
const tiene = (p: { procedimientos: string[] }, x: string) => p.procedimientos.includes(x);
const soloFrenectomia = (p: PlanCirugia) => tiene(p, FRENECTOMIA) && p.procedimientos.length === 1;
const nombreZona = (p: PlanCirugia) => [p.piezas.length ? `pieza${p.piezas.length > 1 ? "s" : ""} ${p.piezas.join(", ")}` : "", p.zona].filter(Boolean).join(" · ");
export const zonaCirugia = nombreZona;

export function problemasPlanCirugia(p: PlanCirugia): string[] {
  const e: string[] = [];
  if (!p.procedimientos.length) e.push("Elige al menos un procedimiento.");
  if (!p.piezas.length && !p.zona.trim()) e.push("Indica la pieza o la zona (por ejemplo: frenillo labial superior, torus palatino).");
  if (p.piezas.some((n) => !piezaCirugia(n))) e.push("Revisa los números de pieza (sistema FDI).");
  if (new Set(p.piezas).size !== p.piezas.length) e.push("Hay una pieza repetida.");
  if (tiene(p, MOLAR) && (!p.claseMolar || !p.posicionMolar)) e.push("Tercer molar: indica la clase y la posición de Pell & Gregory.");
  if (tiene(p, SUPERNUMERARIO) && !p.supernumerario) e.push("Supernumerario: indica si es mesiodens, paramolar o distomolar.");
  if (tiene(p, FRENECTOMIA) && !p.frenectomia) e.push("Frenectomía: indica si es labial o lingual.");
  return e;
}

export function avisosPlanCirugia(p: PlanCirugia): string[] {
  const a: string[] = [];
  if (tiene(p, MOLAR)) {
    if (p.piezas.some((n) => ![18, 28, 38, 48].includes(n))) a.push("Tercer molar: las piezas habituales son 18, 28, 38 y 48.");
    if (!p.winter) a.push("Falta la clasificación de Winter del tercer molar.");
    if ((p.claseMolar === "Clase III" && p.posicionMolar === "C") || p.winter === "Distoangular" || p.winter === "Horizontal")
      a.push("Tercer molar de mayor dificultad: revisa la tomografía y la relación con el nervio dentario inferior.");
  }
  if (tiene(p, CANINO) && p.piezas.some((n) => ![13, 23, 33, 43].includes(n))) a.push("Canino incluido: las piezas habituales son 13, 23, 33 y 43.");
  if (tiene(p, TRACCION) && !tiene(p, CANINO)) a.push("La tracción ortodóncica se coordina con el ortodoncista: confirma el aparato y la fecha de inicio.");
  return a;
}

/** Lista de verificación de los tiempos quirúrgicos según lo planificado. */
export function checklistCirugia(p: PlanCirugia): PasoCheck[] {
  const molar = tiene(p, MOLAR), sup = tiene(p, SUPERNUMERARIO), canino = tiene(p, CANINO), tracc = tiene(p, TRACCION);
  const quiste = tiene(p, QUISTE), apice = tiene(p, APICECTOMIA), freno = tiene(p, FRENECTOMIA), alveolo = tiene(p, ALVEOLOPLASTIA);
  const avulsion = molar || sup || canino, oseo = avulsion || tracc || quiste || apice || alveolo, solo = soloFrenectomia(p);
  const A = "Antes de la cirugía", D = "Durante la cirugía", F = "Al terminar";
  const pasos: (PasoCheck | false)[] = [
    { clave: "consentimiento", texto: "Consentimiento informado de cirugía firmado", fase: A, requerido: true },
    { clave: "antecedentes", texto: "Antecedentes revisados: anticoagulantes, bifosfonatos, diabetes, tabaco, alergias", fase: A, requerido: true },
    oseo && { clave: "imagen", texto: "Radiografía panorámica o tomografía revisada: nervio dentario inferior, seno maxilar y dientes vecinos", fase: A, requerido: true },
    { clave: "premedicacion", texto: "Profilaxis antibiótica o premedicación según el caso", fase: A, requerido: false },
    { clave: "antisepsia", texto: "Antisepsia preoperatoria (clorhexidina o yodopovidona) y equipo de protección estéril completo", fase: A, requerido: true },
    { clave: "pausa", texto: "Pausa de seguridad: paciente, pieza o zona y lado correctos", fase: A, requerido: true },
    { clave: "anestesia", texto: "Anestesia local o bloqueo troncular, con latencia y hemostasia comprobadas", fase: D, requerido: true },
    { clave: "incision", texto: solo ? "Incisión del frenillo" : "Incisión mucosa con la hoja elegida", fase: D, requerido: true },
    !solo && { clave: "colgajo", texto: "Colgajo mucoperióstico despegado a espesor total y retraído", fase: D, requerido: true },
    oseo && { clave: "irrigacion", texto: "Pieza de mano quirúrgica con suero fisiológico helado continuo", fase: D, requerido: false },
    oseo && { clave: "osteotomia", texto: "Osteotomía u osteectomía con fresas e irrigación (despeje pericoronario)", fase: D, requerido: true },
    avulsion && { clave: "odontoseccion", texto: "Odontosección o fragmentación, si se necesitó", fase: D, requerido: false },
    avulsion && { clave: "avulsion", texto: "Luxación y avulsión con los elevadores y el fórceps elegidos", fase: D, requerido: true },
    avulsion && { clave: "remocion", texto: "Remoción completa comprobada, sin fractura del ápice radicular", fase: D, requerido: true },
    tracc && { clave: "exposicion", texto: "Corona expuesta y aislada, con hemostasia y esmalte seco", fase: D, requerido: true },
    tracc && { clave: "grabado", texto: "Grabado ácido al 37 % (15–20 segundos)", fase: D, requerido: true },
    tracc && { clave: "adhesivo", texto: "Primer y adhesivo fotocurados", fase: D, requerido: true },
    tracc && { clave: "boton", texto: "Botón o bracket cementado", fase: D, requerido: true },
    tracc && { clave: "cadeneta", texto: "Cadeneta o ligadura de alambre fijada, con salida limpia por el colgajo", fase: D, requerido: true },
    quiste && { clave: "enucleacion", texto: "Cápsula de la lesión legrada de forma metódica", fase: D, requerido: true },
    quiste && { clave: "biopsia", texto: "Muestra en formol al 10 % enviada a anatomía patológica", fase: D, requerido: true },
    apice && { clave: "apicectomia", texto: "Resección apical y legrado periapical", fase: D, requerido: true },
    apice && { clave: "retrograda", texto: "Obturación retrógrada", fase: D, requerido: true },
    freno && { clave: "frenectomia", texto: "Frenillo liberado por completo (frenectomía o frenotomía)", fase: D, requerido: true },
    alveolo && { clave: "alveoloplastia", texto: "Regularización ósea: espículas limadas (lima para hueso o gubia)", fase: D, requerido: true },
    !solo && { clave: "legrado", texto: "Legrado del lecho: tejido de granulación retirado", fase: D, requerido: false },
    { clave: "lavado", texto: "Lavado abundante del lecho con suero estéril, sin virutas óseas ni detritos", fase: D, requerido: !solo },
    { clave: "hemostaticos", texto: "Hemostático local o material de relleno colocado, si se necesitó", fase: D, requerido: false },
    { clave: "sutura", texto: tracc ? "Colgajo afrontado y suturado, con la cadeneta o ligadura saliendo limpia" : "Colgajo afrontado y suturado", fase: D, requerido: true },
    { clave: "hemostasia", texto: "Hemostasia comprobada", fase: D, requerido: true },
    { clave: "gasa", texto: "Presión firme con gasa estéril humedecida durante 30 a 45 minutos", fase: F, requerido: true },
    { clave: "rx_post", texto: "Radiografía de control (riesgo anatómico, biopsia o tracción)", fase: F, requerido: tracc },
    { clave: "indicaciones", texto: "Indicaciones postoperatorias explicadas y entregadas por escrito", fase: F, requerido: true },
    { clave: "receta", texto: "Receta entregada (analgesia y antiinflamatorio; antibiótico si aplica)", fase: F, requerido: false },
    { clave: "control", texto: "Control agendado (retiro de puntos a los 7–10 días)", fase: F, requerido: true },
  ];
  return pasos.filter((x): x is PasoCheck => !!x);
}

const TERCEROS_MOLARES = [18, 28, 38, 48];
/**
 * Hallazgos del odontograma que genera la cirugía, salvo la extracción de terceros molares (esa se marca como siempre, «Extracción»).
 * Las piezas que no son terceros molares se asignan a los procedimientos de pieza (canino incluido, tracción, apicectomía);
 * quiste, supernumerario, frenectomía y alveoloplastia pueden ir sin pieza (zona).
 */
export function hallazgosCirugia(p: PlanCirugia): { diente: number | null; tipo: string }[] {
  const otras = p.piezas.filter((n) => !TERCEROS_MOLARES.includes(n));
  const res: { diente: number | null; tipo: string }[] = [];
  const poner = (tipo: string, dientes: (number | null)[]) => { for (const diente of dientes) if (!res.some((x) => x.tipo === tipo && x.diente === diente)) res.push({ diente, tipo }); };
  if (tiene(p, SUPERNUMERARIO)) poner("supernumerario", otras.length ? otras : [null]);
  if (tiene(p, CANINO)) poner("incluido", otras);
  if (tiene(p, TRACCION)) poner("traccion", otras);
  if (tiene(p, QUISTE)) poner("quiste", otras.length ? otras : [null]);
  if (tiene(p, APICECTOMIA)) poner("apicectomia", otras.length ? otras : p.piezas);
  if (tiene(p, FRENECTOMIA)) poner("frenectomia", [null]);
  if (tiene(p, ALVEOLOPLASTIA)) poner("alveoloplastia", otras.length ? otras : [null]);
  return res;
}

export interface CirugiaOral {
  fecha: string; hora_inicio: string; hora_fin: string; checklist: string[];
  antisepsia: string; bloqueos: string[]; farmaco: string; cartuchos: number | null; hoja: string; colgajo: string;
  fresas: string[]; odontoseccion: string[]; elevadores: string[]; forceps: string; hemostaticos: string[];
  hilo: string; calibre: string; patron: string; instrumentista: string; complicaciones: string[]; nota: string;
  /** pasos de la lista de verificación que no se marcaron al firmar (se firma igual, pero queda registrado); lo calcula el servidor */
  omitidos?: string[];
}
export const cirugiaOralVacia = (fecha: string): CirugiaOral => ({
  fecha, hora_inicio: "", hora_fin: "", checklist: [], antisepsia: "", bloqueos: [], farmaco: "", cartuchos: null, hoja: "", colgajo: "",
  fresas: [], odontoseccion: [], elevadores: [], forceps: "", hemostaticos: [], hilo: "", calibre: "", patron: "", instrumentista: "", complicaciones: [], nota: "",
});

/** Lo que falta para firmar la cirugía. */
export function problemasCirugiaOral(p: PlanCirugia, c: CirugiaOral): string[] {
  const e: string[] = [];
  if (!c.farmaco) e.push("Indica el anestésico usado.");
  if (c.cartuchos !== null && (!Number.isFinite(c.cartuchos) || c.cartuchos < 0 || c.cartuchos > 10)) e.push("Los cartuchos van de 0 a 10.");
  if (!soloFrenectomia(p) && !c.colgajo) e.push("Indica el diseño del colgajo.");
  if (!c.hilo) e.push("Indica el hilo de sutura.");
  if (c.hora_inicio && c.hora_fin && c.hora_fin <= c.hora_inicio) e.push("La hora de fin debe ser posterior a la de inicio.");
  return e;
}

export function avisosCirugiaOral(p: PlanCirugia, c: CirugiaOral): string[] {
  const a: string[] = [];
  if (c.complicaciones.includes("Parestesia")) a.push("Hubo parestesia: valora la sensibilidad en el control de la primera semana y explícalo al paciente.");
  if (c.complicaciones.includes("Comunicación oroantral")) a.push("Comunicación oroantral: indica precauciones sinusales y control en pocos días.");
  if (tiene(p, QUISTE) && !c.checklist.includes("biopsia")) a.push("Hay una lesión para biopsia: no olvides enviar la muestra a anatomía patológica.");
  return a;
}

const sumarDias = (iso: string, dias: number) => new Date(Date.parse(iso + "T12:00:00Z") + dias * 86400000).toISOString().slice(0, 10);

/** Fechas orientativas desde la cirugía: retiro de puntos (8 días), informe de patología (15 días) y control de la tracción (3 semanas). */
export function fechasCirugiaOral(p: PlanCirugia, fecha: string): { retiroPuntos: string; resultadoPatologia?: string; controlTraccion?: string } {
  return {
    retiroPuntos: sumarDias(fecha, 8),
    ...(tiene(p, QUISTE) ? { resultadoPatologia: sumarDias(fecha, 15) } : {}),
    ...(tiene(p, TRACCION) ? { controlTraccion: sumarDias(fecha, 21) } : {}),
  };
}

const lista = (x: string[]) => x.map((k) => k.toLowerCase()).join(", ");

/** Texto de la indicación (clasificaciones) para el resumen y la ficha. */
export function indicacionCirugia(p: PlanCirugia): string {
  const partes = [...p.procedimientos.map((x) => x.toLowerCase())];
  const det = [
    tiene(p, MOLAR) && `tercer molar: Pell & Gregory ${p.claseMolar.toLowerCase()}, posición ${p.posicionMolar}${p.winter ? `, Winter ${p.winter.toLowerCase()}` : ""}`,
    tiene(p, SUPERNUMERARIO) && p.supernumerario && `supernumerario: ${p.supernumerario.toLowerCase()}`,
    tiene(p, FRENECTOMIA) && p.frenectomia && `frenillo ${p.frenectomia.toLowerCase()}`,
  ].filter(Boolean);
  return `${partes.join("; ")}${det.length ? ` (${det.join("; ")})` : ""}`;
}

/** Nota de evolución de la cirugía. */
export function resumenCirugiaOral(p: PlanCirugia, c: CirugiaOral): string {
  const partes = [
    `Cirugía oral · ${nombreZona(p)}.`,
    `Procedimiento: ${indicacionCirugia(p)}.`,
    c.antisepsia && `Antisepsia: ${c.antisepsia.toLowerCase()}.`,
    (c.farmaco || c.bloqueos.length) && `Anestesia: ${[c.bloqueos.length ? lista(c.bloqueos) : "", c.farmaco, c.cartuchos ? `${c.cartuchos} cartucho(s)` : ""].filter(Boolean).join(", ")}.`,
    (c.hoja || c.colgajo) && `Incisión${c.hoja ? ` con hoja ${c.hoja}` : ""}${c.colgajo ? `, colgajo ${c.colgajo.toLowerCase()}` : ""}.`,
    c.fresas.length > 0 && `Fresas: ${lista(c.fresas)}.`,
    c.odontoseccion.length > 0 && `Técnica: ${lista(c.odontoseccion)}.`,
    (c.elevadores.length > 0 || c.forceps) && `Instrumental de avulsión: ${[c.elevadores.length ? lista(c.elevadores) : "", c.forceps ? `fórceps ${c.forceps}` : ""].filter(Boolean).join(", ")}.`,
    c.hemostaticos.length > 0 && `Hemostáticos y relleno: ${lista(c.hemostaticos)}.`,
    c.hilo && `Sutura: ${c.hilo.toLowerCase()}${c.calibre ? ` ${c.calibre}` : ""}${c.patron ? `, ${c.patron.toLowerCase()}` : ""}.`,
    tiene(p, QUISTE) && c.checklist.includes("biopsia") && "Muestra en formol al 10 % para anatomía patológica.",
    c.instrumentista && `Instrumentista: ${c.instrumentista}.`,
    c.hora_inicio && c.hora_fin && `Duración ${c.hora_inicio}–${c.hora_fin}.`,
    c.complicaciones.length ? `Incidencias: ${lista(c.complicaciones)}.` : "Sin incidencias.",
    c.omitidos?.length ? `Pasos de la lista de verificación sin marcar: ${c.omitidos.join("; ")}.` : "",
    c.nota,
  ];
  return partes.filter(Boolean).join(" ");
}

export interface ControlCirugia { fecha: string; tipo: string; complicaciones: string[]; nota: string }

export function problemasControlCirugia(x: ControlCirugia): string[] {
  const e: string[] = [];
  if (!(TIPOS_CONTROL_CIRUGIA as readonly string[]).includes(x.tipo)) e.push("Elige el tipo de control.");
  if (x.tipo === "Resultado de patología" && !x.nota.trim()) e.push("Escribe el diagnóstico del informe de patología en la nota.");
  return e;
}
export function alertasControlCirugia(x: ControlCirugia): string[] {
  const a: string[] = [];
  if (x.complicaciones.includes("Alveolitis seca")) a.push("Alveolitis seca: irrigar, colocar apósito analgésico y revisar en 48 horas.");
  if (x.complicaciones.includes("Infección")) a.push("Infección: valora antibiótico y drenaje, y controla en 48 horas.");
  if (x.complicaciones.includes("Parestesia persistente")) a.push("Parestesia persistente: documenta la zona y seguimiento de la sensibilidad.");
  return a;
}
export function resumenControlCirugia(p: PlanCirugia, x: ControlCirugia): string {
  return `Control de cirugía oral (${x.tipo.toLowerCase()}) · ${nombreZona(p)}. ${x.complicaciones.length ? `Complicaciones: ${lista(x.complicaciones)}.` : "Sin complicaciones."}${x.nota ? ` ${x.nota}` : ""}`;
}

/** Pasos obligatorios de la lista de verificación que no se marcaron (se puede firmar igual; quedan escritos en la evolución). */
export function pasosOmitidosCirugia(p: PlanCirugia, c: CirugiaOral): PasoCheck[] {
  return checklistCirugia(p).filter((x) => x.requerido && !c.checklist.includes(x.clave));
}
